import React from "react";
import { useSnapshot } from "valtio";
import classnames from "classnames";
import { useTranslation } from "react-i18next/";
import { CheckboxIcon, VerticalDotsIcon } from "@aureskonnect/react-ui";
import { toast } from "react-toastify";
import { uuid } from "uuidv4";

import ErrorToast from "@components/Common/ErrorTost";
import { useFetchGlobal } from "../../../../api/hooks";

import store, {
    setIsLoading,
    setKey,
    setFnDelete,
    setIsDeleteCheckClick,
} from "../store";

import { store as ModeOfInformation } from "../../../../components/VerticalLayout/store";
import { generalConfigStore, setItemsDisable } from "@store";
import {
    ArchiveModeOfInformationFranchise,
    groupByShopId,
    isInformationModeConsumed,
} from "../helper";

export function ActionColumnModeInformation({
    setIsHistoryModalOpened,
    isHistoryModalOpened,
    selectedRows,
    isModeOfInformationArchived,
    setIsModeOfInformationArchived,
    selectedIds,
    setLimit,
}: any) {
    const { t } = useTranslation();
    const {
        numberActiveMultiShop,
        numberArchiveMultiShop,
        archivedModeInformationFranchise,
        modify,
        isLoading,
    } = useSnapshot(store);
    const { itemsDisable } = useSnapshot(generalConfigStore);
    const { fetchGlobal } = useFetchGlobal();

    const { franchiseID, userID, operatorID } = useSnapshot(ModeOfInformation);
    const i18nextLng = localStorage.getItem("i18nextLng") || "fr";
    const uuidUser = localStorage.getItem("uuidUser");

    const getArrayOfRows = () => {
        let arrayOfRows: any[] = [];
        let existArrayOfRows: any[] = [];
        selectedRows.forEach((el: any) => {
            if (
                existArrayOfRows.indexOf(`${el.Id}${el.shopId}`) === -1 &&
                el[t("Shop")] === undefined
            ) {
                existArrayOfRows.push(`${el.Id}${el.shopId}`);
                arrayOfRows.push(el);
            }
        });

        return arrayOfRows;
    };

    async function archiveModeOfInformation() {
        setIsLoading(true);
        toast.dismiss();

        const arrayOfRows = getArrayOfRows();
        const validRows = arrayOfRows.filter((el: any) => el.Id !== undefined);

        if (validRows.length === 0) {
            setIsLoading(false);
            return;
        }

        const shopIds = [...new Set(arrayOfRows.map((el) => el.shopId))];
        const queryParam = encodeURIComponent(JSON.stringify(shopIds));

        const dataConsumed: any = await isInformationModeConsumed(
            queryParam,
            franchiseID,
            fetchGlobal
        );

        const consumedSet = new Set(
            Object.entries(
                dataConsumed
            ).flatMap(([shopId, items]: [string, any]) =>
                items.map((item: any) => `${shopId}::${item.id}`)
            )
        );

        const itemsDisableMap = new Map(
            itemsDisable.map((item: any) => [
                `${item.id}::${item.shopId}`,
                item,
            ])
        );

        let isSomeConsumed = false;
        const localData: LocalData = [];
        const itemKeysToRemove = new Set<string>();

        for (const el of validRows) {
            const isConsumed = consumedSet.has(`${el.shopId}::${el.id}`);

            if (isConsumed) {
                isSomeConsumed = true;
            } else {
                localData.push({
                    userId: userID,
                    operatorId: operatorID,
                    shopId: [el.shopId],
                    franchiseId: franchiseID,
                    uuidUser: uuidUser,
                    isShop: true,
                    data: {
                        [el.Id]: {
                            designation: el[t("Mode of information")],
                            idMI: el?.idMI,
                            supportOfSale: el[t("Sales support")],
                            salesSupportData: [el[t("Sales support")]],
                            displayName: el[t("Display name")],
                            modeOfSale: el[t("Sales method")],
                            tag: el.tag,
                            isArchived: true,
                            state: false,
                            shopId: el.shopId,
                            stateShop: el.state,
                            advancedDisplayName: el.advancedDisplayName,
                        },
                    },
                });
            }

            const itemKey = `${el.Id}::${el.shopId}`;
            if (itemsDisableMap.has(itemKey)) {
                itemKeysToRemove.add(itemKey);
            }
        }

        if (itemKeysToRemove.size > 0) {
            const updatedItems = itemsDisable.filter(
                (item: any) =>
                    !itemKeysToRemove.has(`${item.id}::${item.shopId}`)
            );
            setItemsDisable(updatedItems);
            localStorage.setItem("itemsDisable", JSON.stringify(updatedItems));
        }

        try {
            if (localData.length > 0) {
                const groupedData = groupByShopId(localData);
                const response: any = await fetchGlobal(
                    `${process.env.REACT_APP_API_V2_URL}/settings/general/mode_of_Information`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(groupedData),
                    }
                );

                if (!response.error) {
                    toast.success(
                        t(
                            "The Information mode has been successfully archived"
                        )!,
                        {
                            position: toast.POSITION.TOP_CENTER,
                            theme: "colored",
                            icon: (
                                <CheckboxIcon
                                    height={25}
                                    width={25}
                                    fill="white"
                                />
                            ),
                        }
                    );
                    setKey(uuid());
                }
            }

            if (isSomeConsumed) {
                ErrorToast(
                    t(
                        "Please note, you cannot archive an element used in a template"
                    )
                );
            }
        } catch (e) {
            toast.error(`${t("There's an error")!}`, {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            setIsLoading(false);
        }
    }

    async function activeModeOfInformation() {
        setIsLoading(true);
        toast.dismiss();

        const arrayOfRows = getArrayOfRows();
        const validRows = arrayOfRows.filter((el: any) => el.Id !== undefined);

        if (validRows.length === 0) {
            setIsLoading(false);
            return;
        }

        const itemsDisableSet = new Set(
            itemsDisable.map((item: any) => `${item.id}::${item.shopId}`)
        );

        const localData: LocalData = [];
        const newDisabledItems: any[] = [];

        for (const el of validRows) {
            localData.push({
                userId: userID,
                operatorId: operatorID,
                uuidUser: uuidUser,
                shopId: [el.shopId],
                franchiseId: franchiseID,
                isShop: true,
                data: {
                    [el.Id]: {
                        designation: el[t("Mode of information")],
                        idMI: el?.idMI,
                        supportOfSale: el[t("Sales support")],
                        salesSupportData: [el[t("Sales support")]],
                        displayName: el[t("Display name")],
                        modeOfSale: el[t("Sales method")],
                        tag: el.tag,
                        isArchived: false,
                        state: el.state,
                        shopId: el.shopId,
                        stateShop: el.state,
                        advancedDisplayName: el.advancedDisplayName,
                    },
                },
            });

            const key = `${el.Id}::${el.shopId}`;
            if (el.state === false && !itemsDisableSet.has(key)) {
                newDisabledItems.push({
                    id: el.Id,
                    state: false,
                    shopId: el.shopId,
                });
                itemsDisableSet.add(key);
            }
        }

        if (newDisabledItems.length > 0) {
            const updatedItems = [...itemsDisable, ...newDisabledItems];
            setItemsDisable(updatedItems);
            localStorage.setItem("itemsDisable", JSON.stringify(updatedItems));
        }

        const groupedData = groupByShopId(localData);

        try {
            const response: any = await fetchGlobal(
                `${process.env.REACT_APP_API_V2_URL}/settings/general/mode_of_Information`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(groupedData),
                }
            );

            if (!response.error) {
                toast.success(
                    t("Information mode has been successfully unarchived")!,
                    {
                        position: toast.POSITION.TOP_CENTER,
                        theme: "colored",
                        icon: (
                            <CheckboxIcon height={25} width={25} fill="white" />
                        ),
                    }
                );
                setKey(uuid());
            }
        } catch (e) {
            toast.error(`${t("There's an error")!}`, {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            setIsLoading(false);
        }
    }
    async function isModeOfSaleArchived() {
        toast.dismiss();
        let isModeOfSaleArchived = false;
        const url = `${process.env.REACT_APP_API_V2_URL}/settings/general/mode_of_sale_archived?userId=${userID}&franchiseId=${franchiseID}&uuidUser=${uuidUser}`;
        try {
            const data: any = await fetchGlobal(url, {
                method: "GET",
                headers: { "Accept-Language": i18nextLng },
            });

            data.data.forEach((elt: any) => {
                selectedRows.forEach((element: any) => {
                    element[t("Sales method")].forEach((sale: any) => {
                        if (
                            elt.subRows.some(
                                (saleMethod: any) =>
                                    `${saleMethod["Mode of sale"]} ${saleMethod.shopId}` ===
                                    `${sale.label} ${element.shopId}`
                            )
                        ) {
                            isModeOfSaleArchived = true;
                        }
                    });
                });
            });
        } catch (error) {
            console.error("Failed to process mode of sale archival");
        }
        return isModeOfSaleArchived;
    }

    return (
        <React.Fragment>
            <div>
                <div className="dropdown cmn_drp_BTmenu">
                    <VerticalDotsIcon
                        id="dropdownMenuButton1"
                        data-bs-toggle="dropdown"
                        style={{ cursor: "pointer" }}
                        height={25}
                        width={25}
                        fill="black"
                    />
                    <div
                        className="dropdown-menu"
                        aria-labelledby="dropdownMenuButton"
                    >
                        <div
                            className="dropdown-item pl-1"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                                setIsModeOfInformationArchived(false);
                            }}
                        >
                            <span className="ml-2 cmn_btn_BTlistActivated">
                                {t("Activated mode of information")}(s) (
                                {numberActiveMultiShop
                                    ? numberActiveMultiShop
                                    : 0}
                                )
                            </span>
                        </div>
                        <div
                            className="dropdown-item pl-1"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                                setIsModeOfInformationArchived(true);
                            }}
                        >
                            <span className="ml-2 cmn_btn_BTlistArchived">
                                {t("Archived mode of information")}(s) (
                                {numberArchiveMultiShop
                                    ? numberArchiveMultiShop
                                    : 0}
                                )
                            </span>
                        </div>
                        <div
                            style={{ cursor: "pointer" }}
                            className={`${classnames(
                                " dropdown-item pl-1 pointer__clz cmn_btn_BThistory",
                                {
                                    "not-allowed-icon__clz":
                                        selectedRows.length === 0,
                                }
                            )}`}
                            onClick={() => {
                                if (selectedIds.length <= 1) {
                                    ErrorToast(
                                        `${t(
                                            "Attention, it is a multiple action. Please select at least two items"
                                        )!}`
                                    );
                                } else {
                                    setIsHistoryModalOpened(
                                        !isHistoryModalOpened
                                    );
                                }
                            }}
                        >
                            <span className="ml-2">{t("Historical")}</span>
                        </div>
                        {modify === true ? (
                            <>
                                <div
                                    style={{ cursor: "pointer" }}
                                    className={`${classnames(
                                        " dropdown-item pl-1 pointer__clz cmn_btn_BTarchived cmn_btn_BTunarchive",
                                        {
                                            "not-allowed-icon__clz":
                                                isLoading ||
                                                selectedIds.length === 0 ||
                                                (isModeOfInformationArchived &&
                                                    getArrayOfRows().some(
                                                        (element: any) =>
                                                            ArchiveModeOfInformationFranchise(
                                                                archivedModeInformationFranchise
                                                            ).includes(
                                                                element.Id
                                                            )
                                                    )),
                                        }
                                    )}`}
                                    onClick={async () => {
                                        setIsLoading(true);
                                        let isArchived = await isModeOfSaleArchived();
                                        if (selectedIds.length <= 1) {
                                            ErrorToast(
                                                `${t(
                                                    "Attention, it is a multiple action. Please select at least two items"
                                                )!}`
                                            );
                                            setIsLoading(false);
                                        } else if (
                                            isArchived === true &&
                                            isModeOfInformationArchived
                                        ) {
                                            ErrorToast(
                                                `${t(
                                                    "Please note that  the unarchiving process could not be successfully completed. Please check the settings of the store(s) data."
                                                )!}`
                                            );
                                            setIsLoading(false);
                                        } else {
                                            if (
                                                isModeOfInformationArchived ===
                                                true
                                            ) {
                                                activeModeOfInformation();
                                            } else {
                                                setIsLoading(false);
                                                setIsDeleteCheckClick(true);
                                                setFnDelete(async () => {
                                                    archiveModeOfInformation();
                                                });
                                            }
                                        }
                                    }}
                                >
                                    <span className="ml-2">
                                        {isModeOfInformationArchived
                                            ? t("Unarchive")
                                            : t("Archiver")}
                                    </span>
                                </div>
                            </>
                        ) : null}
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}
